import { useEffect, useMemo, useState } from 'react'
import { Grid, List } from 'lucide-react'
import { Navigate, useNavigate } from 'react-router'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Loading from '@/elements/Loading'
import Text from '@/elements/Text'
import { GetTheAppModalContent } from '@/components/GetTheAppModal'
import Table from '@/components/Table'
import FilterBar from '@/components/DashboardFilterBar'
import TalentCard from '@/components/TalentCard'
import PushNotificationPopUp from '@/components/PushNotificationPopUp'
import ExportModal from '@/components/ExportModal'
import { talentTableColumns } from '@/utils/tables/talentTable'
import { getUserLocation } from '@/utils/location'
import { useGetAccessibleSubjects, useGetSubjectGroups } from '@/api/profile'
import useAuthStore from '@/stores/authStore'
import useRoles from '@/hooks/useRoles'
import usePagination from '@/hooks/usePagination'
import Pagination from '@/components/Pagination'
import useFiltering from '@/hooks/useFiltering'
import useBreakpoints from '@/hooks/useBreakpoints'

export const viewOptions = [
  { value: 'grid', Icon: Grid },
  { value: 'list', Icon: List },
]
export type ViewOptionsType = typeof viewOptions[number]['value']

function Dashboard() {
  const [talentView, setTalentView] = useState<ViewOptionsType>('grid')
  const [showExportModal, setShowExportModal] = useState(false)
  const navigate = useNavigate()
  const { user, hasOrganizations } = useAuthStore()
  const { isSubject } = useRoles()
  const { isMobile } = useBreakpoints()
  const { page, limit, setPage } = usePagination(talentView === 'grid' ? 9 : 10)
  const { updateSortBy, updateFilterBy, sortBy, sortDir, allFilters } = useFiltering<['name', 'groupName']>()
  const subjectId = user?.subject?.id

  const { data: groupOptions } = useGetSubjectGroups({ assigned: true })

  const { data: accessibleSubjects, isLoading } = useGetAccessibleSubjects(
    {
      page, limit,
      filters: {
        ...(allFilters.name ? { 'filter[name]': allFilters.name } : {}),
        ...(allFilters.groupName ? { 'filter[groupName]': allFilters.groupName } : {}),
        ...(sortBy ? { [`sort[${sortBy}]`]: sortDir || 'ASC' } : {}),
      }
    },
    hasOrganizations
  )

  const subjects = useMemo(() => {
    return accessibleSubjects?.data?.items || []
  }, [accessibleSubjects])

  const hasFilters = useMemo(() => {
    return Object.values(allFilters).some(v => v)
  }, [allFilters])

  useEffect(() => {
    if (isMobile) {
      setTalentView('grid')
      localStorage.setItem('talentView', JSON.stringify('grid'))
    } else {
      const storedState = localStorage.getItem('talentView')
      if (storedState !== null) {
        setTalentView(JSON.parse(storedState))
      }
    }
  }, [isMobile])

  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem('talentView', JSON.stringify(talentView))
    }
  }, [talentView, isMobile])

  const tableData = useMemo(() => subjects.map(row => ({
    id: row.id,
    name: row.user?.details?.fullName,
    group: row.group?.name,
    location: getUserLocation(row.user?.details),
    alertsCount: row.alertsCount,
    avatarImg: row.user?.assets?.PROFILE_PICTURE,
  })) || [], [subjects])

  const handleClearFilters = () => {
    if (subjects.length === 0) {
      navigate('/dashboard')
    }
  }

  if (isSubject) {
    return <Navigate to={`/watchdog/${subjectId}`} />
  }

  if (!hasOrganizations) {
    return (
      <Flex component='section' gap={4} grow>
        <Flex wfull grow justify='center' align='center' gap={3} pb={4}>
          <GetTheAppModalContent />
          <Text align='center'>
            You are not part of any organization. Please contact your administrator.
          </Text>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex component='section' gap={4} grow>
      <Flex gap={4} wfull>
        <PushNotificationPopUp />
        <Text variant='h1'>Dashboard</Text>
        <FilterBar
          view={talentView}
          onViewChange={setTalentView}
          filterByName={allFilters.name || ''}
          setFilterByName={q => updateFilterBy('name', q)}
          filterByGroupName={allFilters.groupName || ''}
          setFilterByGroupName={q => updateFilterBy('groupName', q)}
          groupOptions={groupOptions?.data || []}
          sortDir={sortDir}
          sortBy={sortBy}
          setSortBy={updateSortBy}
          sortByOptions={accessibleSubjects?.data?.filters?.sortableBy}
          onExport={() => setShowExportModal(true)}
        />
      </Flex>
      {isLoading ? (
        <Loading />
      ) : talentView === 'grid' ? (
        <Flex
          gap={3}
          wfull
          grid
          cols='repeat(auto-fill, minmax(300px, 1fr))'
        >
          {subjects.map(subject => (
            <TalentCard
              key={subject.id}
              subject={subject}
              notificationCount={subject.alertsCount}
            />
          ))}
        </Flex>
      ) : (
        <Flex wfull>
          <Table
            data={tableData}
            columns={talentTableColumns}
          />
        </Flex>
      )}
      {(!subjects.length && !isLoading && talentView === 'grid') && (
        <Flex wfull justify='center' align='center' py={4} gap={3} grow>
          {!hasFilters ? (
            <Text color='black-light-60'>No subjects available. Please add subjects to your organization.</Text>
          ) : (
            <Flex gap={3} align='center' p={4}>
              <Text color='black-light-60'>No subjects found matching the applied filters.</Text>
              <Button variant='primary' onClick={handleClearFilters}>Clear filters</Button>
            </Flex>
          )}
        </Flex>
      )}
      <Flex wfull align='center' pt={4} mt='auto'>
        <Pagination
          variant={isMobile ? 'compact' : 'default'}
          onPageChange={setPage}
          {...accessibleSubjects?.data?.pagination}
        />
      </Flex>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        mode='organization'
      />
    </Flex>
  )
}

export default Dashboard
